import { Button } from '@neo/primitives/button'
import { Input } from '@neo/primitives/input'
import { Popover, PopoverClose, PopoverContent, PopoverTrigger } from '@neo/primitives/popover'
import { Typography } from '@neo/primitives/typography'
import { Search, X } from 'lucide-react'
import { useState } from 'react'
import type { RActionDetails } from 'v2/types/api/settings'

const humanizeActionName = (actionName: string): string => {
  return actionName
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

export interface ActionsPopoverProps {
  appName: string
  directActions: RActionDetails[]
  children: React.ReactNode
}

export const ActionsPopover = ({ appName, directActions, children }: ActionsPopoverProps) => {
  const [search, setSearch] = useState('')
  const [isOpen, setIsOpen] = useState(false)

  const filteredActions = directActions.filter((action) =>
    action.action_name.toLowerCase().includes(search.toLowerCase()),
  )

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        className="w-[500px] max-h-96 overflow-hidden"
        align="end"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col gap-4 mb-4">
          <div className="flex items-center justify-between">
            <div>
              <Typography variant="H4/500">{appName} Actions</Typography>
            </div>
            <PopoverClose asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0"
                data-testid="close-actions-popover"
              >
                <X size={20} />
                <span className="sr-only">Close</span>
              </Button>
            </PopoverClose>
          </div>
          <Input
            placeholder="Search"
            className="w-full"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            startIcon={Search}
          />
        </div>
        <div className="overflow-y-auto max-h-64 gap-3 flex flex-col">
          {filteredActions.length === 0 && (
            <div className="text-center py-4">
              <Typography variant="S/400" className="text-secondary">
                No actions available for this integration
              </Typography>
            </div>
          )}
          {filteredActions.map((action) => (
            <div key={action.action_name} className="space-y-1">
              <Typography variant="S/500">{humanizeActionName(action.action_name)}</Typography>
              {action.description && (
                <Typography variant="XS/400" className="text-tertiary">
                  {action.description}
                </Typography>
              )}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}
